import { API_URL } from '../utils/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, Pencil, Trash2, Calendar, CheckCircle2, Circle, User,
  Clock, AlertCircle, Users, ThumbsUp, ThumbsDown, Send, Eye,
  ChevronRight, UserPlus, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../lib/utils';


const PRIORITY_CONFIG = {
  low: { label: 'Düşük', color: 'bg-gray-100 text-gray-700', border: 'border-l-gray-400' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700', border: 'border-l-blue-400' },
  high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-700', border: 'border-l-orange-400' },
  urgent: { label: 'Acil', color: 'bg-red-100 text-red-700', border: 'border-l-red-500' }
};

const STATUS_CONFIG = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-700', icon: Circle },
  in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700', icon: Clock },
  awaiting_approval: { label: 'Onay Bekliyor', color: 'bg-purple-100 text-purple-700', icon: Send },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

const TaskManagement = () => {
  const { user, token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [approvalNote, setApprovalNote] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'normal',
    status: 'pending',
    assigned_to: '',
    assigned_to_name: '',
    tags: [],
    requires_approval: false
  });

  const canManage = user?.permissions?.includes('all') || user?.permissions?.includes('workspace_manage');
  const canView = user?.permissions?.includes('all') || user?.permissions?.includes('workspace_view') || canManage;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tasks`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers }),
        axios.get(`${API_URL}/api/tasks/stats/summary`, { headers })
      ]);

      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (canView) fetchData();
  }, [canView, fetchData]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      due_date: '',
      due_time: '',
      priority: 'normal',
      status: 'pending',
      assigned_to: '',
      assigned_to_name: '',
      tags: [],
      requires_approval: false
    });
    setEditingTask(null);
  };

  const openModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date || '',
        due_time: task.due_time || '',
        priority: task.priority || 'normal',
        status: task.status || 'pending',
        assigned_to: task.assigned_to || '',
        assigned_to_name: task.assigned_to_name || '',
        tags: task.tags || [],
        requires_approval: task.requires_approval || false
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const openDetailModal = (task) => {
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const openApprovalModal = (task) => {
    setSelectedTask(task);
    setApprovalNote('');
    setIsApprovalModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Görev başlığı zorunludur');
      return;
    }

    try {
      const payload = { ...formData };
      
      // assigned_to_name'i users'dan bul
      if (formData.assigned_to) {
        const assignedUser = users.find(u => u.id === formData.assigned_to);
        payload.assigned_to_name = assignedUser?.name || '';
      }

      if (editingTask) {
        await axios.put(`${API_URL}/api/tasks/${editingTask.id}`, payload, { headers });
        toast.success('Görev güncellendi');
      } else {
        await axios.post(`${API_URL}/api/tasks`, payload, { headers });
        toast.success('Görev oluşturuldu');
      }
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: newStatus }, { headers });
      toast.success('Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleRequestApproval = async (taskId) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}/request-approval`, {}, { headers });
      toast.success('Onay isteği gönderildi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onay isteği gönderilemedi');
    }
  };

  const handleApproval = async (approved) => {
    if (!selectedTask) return;
    try {
      await axios.put(`${API_URL}/api/tasks/${selectedTask.id}/approve`, 
        { approved, note: approvalNote }, 
        { headers }
      );
      toast.success(approved ? 'Görev onaylandı' : 'Görev reddedildi');
      setIsApprovalModalOpen(false);
      setIsDetailModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, { headers });
      toast.success('Görev silindi');
      setIsDetailModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const isOverdue = (task) => {
    if (!task.due_date || ['completed', 'rejected'].includes(task.status)) return false;
    return new Date(task.due_date) < new Date(new Date().toDateString());
  };

  // Filtreleme
  const filteredTasks = tasks.filter(t => {
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchAssigned = filterAssigned === 'all' || 
      (filterAssigned === 'me' && t.assigned_to === user?.id) ||
      (filterAssigned === 'unassigned' && !t.assigned_to) ||
      t.assigned_to === filterAssigned;
    return matchStatus && matchAssigned;
  });

  // Gruplama: Onay Bekleyenler, Bana Atananlar, Diğer
  const pendingApprovals = filteredTasks.filter(t => t.status === 'awaiting_approval');
  const myTasks = filteredTasks.filter(t => t.assigned_to === user?.id && t.status !== 'awaiting_approval');
  const otherTasks = filteredTasks.filter(t => t.assigned_to !== user?.id && t.status !== 'awaiting_approval');

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  const TaskCard = ({ task }) => {
    const StatusIcon = STATUS_CONFIG[task.status]?.icon || Circle;
    return (
      <div
        className={cn(
          "bg-background rounded-lg p-4 shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-all",
          PRIORITY_CONFIG[task.priority]?.border || 'border-l-gray-300',
          isOverdue(task) && "bg-red-50 dark:bg-red-900/20"
        )}
        onClick={() => openDetailModal(task)}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className={cn(
            "font-medium",
            task.status === 'completed' && "line-through text-muted-foreground"
          )}>
            {task.title}
          </h3>
          <Badge className={STATUS_CONFIG[task.status]?.color}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {STATUS_CONFIG[task.status]?.label}
          </Badge>
        </div>
        
        {task.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
        )}
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {task.assigned_to_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {task.assigned_to_name}
              </span>
            )}
            {task.due_date && (
              <span className={cn(
                "flex items-center gap-1",
                isOverdue(task) && "text-red-500"
              )}>
                <Calendar className="h-3 w-3" />
                {formatDate(task.due_date)}
              </span>
            )}
          </div>
          <Badge className={PRIORITY_CONFIG[task.priority]?.color} variant="outline">
            {PRIORITY_CONFIG[task.priority]?.label}
          </Badge>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Görev Yönetimi</h1>
          <p className="text-muted-foreground">Görevleri atayın ve takip edin</p>
        </div>
        {canManage && (
          <Button onClick={() => openModal()}>
            <Plus className="h-4 w-4 mr-2" />
            Yeni Görev
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={stats.overdue > 0 ? 'border-red-300' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className={cn("h-5 w-5", stats.overdue > 0 ? "text-red-500" : "text-muted-foreground")} />
                <div>
                  <p className="text-sm text-muted-foreground">Gecikmiş</p>
                  <p className="text-xl font-bold">{stats.overdue}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Bugün</p>
                  <p className="text-xl font-bold">{stats.due_today}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Bana Atanan</p>
                  <p className="text-xl font-bold">{stats.my_assigned}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {canManage && (
            <Card className={stats.pending_approvals > 0 ? 'border-purple-300' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Send className={cn("h-5 w-5", stats.pending_approvals > 0 ? "text-purple-500" : "text-muted-foreground")} />
                  <div>
                    <p className="text-sm text-muted-foreground">Onay Bekleyen</p>
                    <p className="text-xl font-bold">{stats.pending_approvals}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <Filter className="h-4 w-4 mr-1" />
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="pending">Bekliyor</SelectItem>
            <SelectItem value="in_progress">Devam Ediyor</SelectItem>
            <SelectItem value="awaiting_approval">Onay Bekliyor</SelectItem>
            <SelectItem value="completed">Tamamlandı</SelectItem>
            <SelectItem value="rejected">Reddedildi</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterAssigned} onValueChange={setFilterAssigned}>
          <SelectTrigger className="w-40">
            <Users className="h-4 w-4 mr-1" />
            <SelectValue placeholder="Atanan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Herkes</SelectItem>
            <SelectItem value="me">Bana Atanan</SelectItem>
            <SelectItem value="unassigned">Atanmamış</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task Lists */}
      <div className="space-y-6">
        {/* Onay Bekleyenler */}
        {canManage && pendingApprovals.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-500" />
              Onay Bekleyenler ({pendingApprovals.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingApprovals.map(task => <TaskCard key={task.id} task={task} />)}
            </div>
          </div>
        )}

        {/* Bana Atananlar */}
        {myTasks.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Bana Atanan Görevler ({myTasks.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myTasks.map(task => <TaskCard key={task.id} task={task} />)}
            </div>
          </div>
        )}

        {/* Diğer Görevler */}
        {otherTasks.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-500" />
              Diğer Görevler ({otherTasks.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherTasks.map(task => <TaskCard key={task.id} task={task} />)}
            </div>
          </div>
        )}

        {filteredTasks.length === 0 && !loading && (
          <div className="text-center py-12 text-muted-foreground">
            <p>Görev bulunamadı</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Görevi Düzenle' : 'Yeni Görev'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Görev Başlığı *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Saat</Label>
                <Input
                  type="time"
                  value={formData.due_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Öncelik</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Atanan Kişi</Label>
                <Select value={formData.assigned_to || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Atanmamış</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_approval"
                checked={formData.requires_approval}
                onChange={(e) => setFormData(prev => ({ ...prev, requires_approval: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="requires_approval" className="cursor-pointer">
                Tamamlandığında onay gerektirsin
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>İptal</Button>
              <Button type="submit">{editingTask ? 'Güncelle' : 'Oluştur'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Görev Detayı</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedTask.title}</h3>
                <div className="flex gap-2 mt-2">
                  <Badge className={STATUS_CONFIG[selectedTask.status]?.color}>
                    {STATUS_CONFIG[selectedTask.status]?.label}
                  </Badge>
                  <Badge className={PRIORITY_CONFIG[selectedTask.priority]?.color}>
                    {PRIORITY_CONFIG[selectedTask.priority]?.label}
                  </Badge>
                </div>
              </div>

              {selectedTask.description && (
                <div>
                  <Label className="text-muted-foreground">Açıklama</Label>
                  <p className="text-sm">{selectedTask.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Oluşturan</Label>
                  <p>{selectedTask.created_by_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Atanan</Label>
                  <p>{selectedTask.assigned_to_name || 'Atanmamış'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tarih</Label>
                  <p>{formatDate(selectedTask.due_date)} {selectedTask.due_time}</p>
                </div>
                {selectedTask.approved_by_name && (
                  <div>
                    <Label className="text-muted-foreground">Onaylayan</Label>
                    <p>{selectedTask.approved_by_name}</p>
                  </div>
                )}
              </div>

              {selectedTask.approval_note && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <Label className="text-muted-foreground">Onay Notu</Label>
                  <p className="text-sm">{selectedTask.approval_note}</p>
                </div>
              )}

              {/* Durum Değiştirme */}
              {canManage && !['completed', 'rejected'].includes(selectedTask.status) && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground mb-2 block">Durum Değiştir</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.status !== 'in_progress' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(selectedTask.id, 'in_progress')}>
                        <Clock className="h-3 w-3 mr-1" /> Devam Ediyor
                      </Button>
                    )}
                    {selectedTask.requires_approval && selectedTask.status !== 'awaiting_approval' && (
                      <Button size="sm" variant="outline" onClick={() => handleRequestApproval(selectedTask.id)}>
                        <Send className="h-3 w-3 mr-1" /> Onay İste
                      </Button>
                    )}
                    {!selectedTask.requires_approval && (
                      <Button size="sm" variant="outline" className="bg-green-50" onClick={() => handleStatusChange(selectedTask.id, 'completed')}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Tamamla
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Onay/Red - Sadece onay bekleyenler için */}
              {canManage && selectedTask.status === 'awaiting_approval' && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground mb-2 block">Onay İşlemi</Label>
                  <div className="flex gap-2">
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => openApprovalModal(selectedTask)}>
                      <ThumbsUp className="h-4 w-4 mr-1" /> Onayla
                    </Button>
                    <Button variant="destructive" onClick={() => openApprovalModal(selectedTask)}>
                      <ThumbsDown className="h-4 w-4 mr-1" /> Reddet
                    </Button>
                  </div>
                </div>
              )}

              <DialogFooter>
                {canManage && (
                  <>
                    <Button variant="destructive" onClick={() => handleDelete(selectedTask.id)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Sil
                    </Button>
                    <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); openModal(selectedTask); }}>
                      <Pencil className="h-4 w-4 mr-1" /> Düzenle
                    </Button>
                  </>
                )}
                <Button onClick={() => setIsDetailModalOpen(false)}>Kapat</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={isApprovalModalOpen} onOpenChange={setIsApprovalModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Görev Onay İşlemi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <strong>"{selectedTask?.title}"</strong> görevini onaylamak veya reddetmek üzeresiniz.
            </p>
            <div>
              <Label>Not (Opsiyonel)</Label>
              <Textarea
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                placeholder="Onay veya red sebebi..."
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsApprovalModalOpen(false)}>İptal</Button>
              <Button variant="destructive" onClick={() => handleApproval(false)}>
                <ThumbsDown className="h-4 w-4 mr-1" /> Reddet
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleApproval(true)}>
                <ThumbsUp className="h-4 w-4 mr-1" /> Onayla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskManagement;
